import { Grid, Layout, List } from 'lucide-react';
import { Switch } from '../../components/ui/Switch';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useProductSystem } from '../../hooks/useProductSystem';

interface DisplayModeButtonProps {
  mode: 'grid' | 'list' | 'carousel';
  label: string;
  icon: typeof Grid;
  selected: boolean;
  onClick: () => void;
}

function DisplayModeButton({ label, icon: Icon, selected, onClick }: DisplayModeButtonProps) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-2 rounded-md transition ${
        selected ? 'bg-indigo-600 text-white' : 'bg-white/5 text-gray-300 hover:bg-white/10'
      }`}
    >
      <Icon className="w-4 h-4" />
      <span>{label}</span>
    </button>
  );
}

export function ProductDisplaySettings() {
  const { chatbotPublicId } = useChatbot();
  const { settings, updateSettings } = useProductSystem();

  // S'assurer que les valeurs par défaut sont toujours définies
  const displayMode = settings.displayMode || 'grid';
  const gridColumns = settings.gridColumns || 3;
  const spacing = settings.spacing || 4;
  const showPrices = settings.showPrices ?? true;
  const showStock = settings.showStock ?? true;

  const handleDisplayModeChange = async (mode: 'grid' | 'list' | 'carousel') => {
    if (!chatbotPublicId) return;
    await updateSettings(chatbotPublicId, { ...settings, displayMode: mode });
  };

  const handleColumnsChange = async (columns: number) => {
    if (!chatbotPublicId) return;
    await updateSettings(chatbotPublicId, { ...settings, gridColumns: columns });
  };

  const handleSpacingChange = async (spacing: number) => {
    if (!chatbotPublicId) return;
    await updateSettings(chatbotPublicId, { ...settings, spacing });
  };

  const handleToggleSetting = async (setting: 'showPrices' | 'showStock') => {
    if (!chatbotPublicId) return;
    await updateSettings(chatbotPublicId, { ...settings, [setting]: !settings[setting] });
  };

  return (
    <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
      <h3 className="text-lg font-semibold text-white mb-6">Display configuration</h3>
      
      {/* Mode d'affichage */}
      <div className="space-y-6">
        <div>
          <h4 className="text-sm font-medium text-gray-300 mb-4">Display mode</h4>
          <div className="grid grid-cols-3 gap-4">
            <DisplayModeButton 
              mode="grid"
              label="Grid"
              icon={Grid}
              selected={displayMode === 'grid'}
              onClick={() => handleDisplayModeChange('grid')}
            />
            <DisplayModeButton 
              mode="list"
              label="List"
              icon={List}
              selected={displayMode === 'list'}
              onClick={() => handleDisplayModeChange('list')}
            />
            <DisplayModeButton 
              mode="carousel"
              label="Carousel"
              icon={Layout}
              selected={displayMode === 'carousel'}
              onClick={() => handleDisplayModeChange('carousel')}
            />
          </div>
        </div>

        {/* Paramètres de la grille */}
        {displayMode !== 'carousel' && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Number of columns
              </label>
              <input
                type="range"
                min="2"
                max="4"
                value={gridColumns}
                onChange={(e) => handleColumnsChange(parseInt(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-sm text-gray-400 mt-2">
                <span>2</span>
                <span>3</span>
                <span>4</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Spacing
              </label>
              <input
                type="range"
                min="2"
                max="8"
                value={spacing}
                onChange={(e) => handleSpacingChange(parseInt(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-sm text-gray-400 mt-2">
                <span>Small</span>
                <span>Medium</span>
                <span>Large</span>
              </div>
            </div>
          </div>
        )}

        {/* Paramètres d'affichage */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-gray-300 mb-4">Information displayed</h4>
          
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-300">Price</label>
            <Switch 
              checked={showPrices}
              onChange={() => handleToggleSetting('showPrices')}
            />
          </div>

          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-300">Stock</label>
            <Switch 
              checked={showStock}
              onChange={() => handleToggleSetting('showStock')}
            />
          </div>
        </div>
      </div>
    </div>
  );
}